import { formatPrice } from '../../dynamicBundle/index.js';
import { getSessionStorage } from '../../utils';
import {
  DROPDOWN_CONTAINER,
  DROPDOWN_ITEM_SELECTOR,
  DROPDOWN_LIST,
  DROPDOWN_LIST_OPEN,
  DROPDOWN_SELECT_OPTION,
  DROPDOWN_SELECT_OPTION_SELECTED,
  KEY_VOLUME_BUNDLE_COMBOBOX_BUTTON,
  KEY_VOLUME_BUNDLE_RADIO,
  RECOMMENDATION_SELECTOR,
  VOLUME_BUNDLE_ACTION,
  VOLUME_MENU_ITEM_SELECTOR,
  VOLUME_MENU_SELECTOR,
  VOLUME_RADIO_CHECKED,
  VOLUME_RADIO_SELECTOR,
} from '../constants';
import { updateMultipleBundlePrice, updateVolumeBundleData } from '../services/bundle-data';
import { updateBundleUI } from '../ui/bundle-render';

/**
 * @type {import('../index.d').closeAllDropdown}
 */
export const closeAllDropdown = () => {
  const dropdowns = document?.querySelectorAll(`.${DROPDOWN_LIST}.${DROPDOWN_LIST_OPEN}`);

  dropdowns?.forEach(removeClassDropdownListOpening);
};

/**
 * @type {import('../index.d').toggleDropdown}
 */
export const toggleDropdown = (target) => {
  const targetDropdown = target?.closest(`.${DROPDOWN_CONTAINER}`);
  const listEl = targetDropdown?.querySelector(`.${DROPDOWN_LIST}`);
  const isOpen = listEl?.classList.contains(DROPDOWN_LIST_OPEN);
  closeAllDropdown();

  if (!isOpen) {
    listEl?.classList.add(DROPDOWN_LIST_OPEN);
    targetDropdown?.setAttribute('aria-expanded', 'true');
    listEl?.focus();
    const selected =
      listEl.querySelector('[aria-selected="true"]') || listEl.querySelector('[role="option"]');
    selected?.focus();
  } else {
    listEl?.classList.remove(DROPDOWN_LIST_OPEN);
    targetDropdown?.setAttribute('aria-expanded', 'false');

    setTimeout(() => targetDropdown?.focus(), 100);
  }
};

/**
 * @type {import('../index.d').removeClassDropdownListOpening}
 */
const removeClassDropdownListOpening = (dropdown) => {
  return dropdown?.classList?.remove(DROPDOWN_LIST_OPEN);
};

/**
 * @type {import('../index.d').handleDropdownToggle}
 */
export const handleDropdownToggle = (target, combobox) => {
  toggleDropdown(target);
};

/**
 * @type {import('../index.d').selectVariantProduct}
 */

export const selectVariantProduct = async (context, target, wrapper, position) => {
  // Get the current option + dropdown
  const selectedEl = target?.closest(DROPDOWN_ITEM_SELECTOR);
  if (!selectedEl) return;

  const dropdownEl = selectedEl.closest('.boost-sd__dropdown');
  const listEl = selectedEl.closest('.boost-sd__dropdown-option-list');
  if (!dropdownEl || !listEl) return;

  const variantId = Number(selectedEl.dataset?.variantId);
  if (!variantId) return;

  // Change selected in this MAIN list + update text of this MAIN button
  listEl
    .querySelectorAll('.boost-sd__dropdown-option--selected')
    .forEach((el) => el.classList.remove(DROPDOWN_SELECT_OPTION_SELECTED));
  selectedEl.classList.add(DROPDOWN_SELECT_OPTION_SELECTED);

  listEl.querySelectorAll('[role="option"]').forEach((opt) => {
    opt.setAttribute('aria-selected', opt === selectedEl ? 'true' : 'false');
  });

  const buttonText = dropdownEl.querySelector(
    '.boost-sd__dropdown-selected-option .boost-sd__dropdown-option-text'
  );
  if (buttonText) buttonText.textContent = selectedEl.textContent.trim();
  if (buttonText) {
    const newLabel = `Current option: ${selectedEl.textContent.trim()}`;
    buttonText.setAttribute('aria-label', newLabel);
    buttonText.setAttribute('title', selectedEl.textContent.trim());
  }

  // Determine current bundle-item group + is it multiple
  const bundleItem = dropdownEl.closest('.boost-sd__volume-bundle-menu-item');
  const bundleContainer = dropdownEl.closest('.boost-sd__volume-bundle-menu');
  const isMultiple = !!bundleItem?.querySelector('.boost-sd__volume-bundle-menu-variant--multiple');

  // MULTIPLE: recalculate only the price of the GROUP being
  if (isMultiple) {
    updateMultipleBundlePrice(context, bundleContainer, bundleItem);

    dropdownEl.setAttribute('aria-expanded', 'false');
    listEl.classList.remove('open');

    setTimeout(() => {
      dropdownEl.focus();
    }, 150);
    return;
  }

  // SINGLE logic
  const productId = target?.closest(VOLUME_MENU_SELECTOR)?.dataset?.productId;
  const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;

  dropdownEl.setAttribute('aria-expanded', 'false');
  listEl.classList.remove('open');

  const selectedRadio = target
    ?.closest(VOLUME_MENU_SELECTOR)
    ?.querySelector(`${VOLUME_RADIO_SELECTOR}.${VOLUME_RADIO_CHECKED}`);
  const selectedRadioId = selectedRadio ? selectedRadio.closest('input')?.id : null;

  // Update bundle single
  const updatedBundleData = await updateVolumeBundleData(
    context,
    widgetId,
    { optionData: { variantId, productId } },
    VOLUME_BUNDLE_ACTION.changeVariant,
    selectedEl,
    position
  );

  // Select valid option to check again
  const widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};
  const defaultOptionIndex = widgetDesignSettings.bundleHighlightOption;
  const defaultOptionId = updatedBundleData.bundleOptions[defaultOptionIndex - 1]?.id;

  const storedOptionId = getSessionStorage(KEY_VOLUME_BUNDLE_RADIO);

  const validOptionId =
    selectedRadioId && updatedBundleData.bundleOptions.some((opt) => opt.id === selectedRadioId)
      ? selectedRadioId
      : storedOptionId && updatedBundleData.bundleOptions.some((opt) => opt.id === storedOptionId)
        ? storedOptionId
        : defaultOptionId;

  updatedBundleData.bundleOptions = updatedBundleData.bundleOptions.map((option) => ({
    ...option,
    checked: option.id === validOptionId,
  }));

  // Update selectedDropdownOptions for each option according to the selected dropdown
  updatedBundleData.bundleOptions = updatedBundleData.bundleOptions.map((option, index) => {
    const optionItem =
      wrapper?.querySelector?.(`#bundle-menu-item-${index + 1}`) ||
      document.querySelector?.(`#bundle-menu-item-${index + 1}`);
    const selectedInThisOption = optionItem?.querySelector(
      '.boost-sd__dropdown-volume-item.boost-sd__dropdown-option--selected'
    );

    const selectedVariantId =
      selectedInThisOption?.dataset?.variantId ||
      updatedBundleData.matchedProduct?.variants?.[0]?.id;

    // Find the corresponding variant to calculate the price
    const optionVariant =
      updatedBundleData.matchedProduct?.variants?.find(
        (v) => Number(v.id) === Number(selectedVariantId)
      ) || updatedBundleData.matchedProduct?.variants?.[0];

    const originPrice = Number(optionVariant?.price || 0);
    const discount =
      (updatedBundleData.discounts || []).find((d) => Number(d.item) === Number(option.items)) ||
      {};

    const rawOriginalPrice = originPrice * Number(option.items || 1);
    const rawDiscountedPrice =
      discount.discount_type === 'percentage'
        ? rawOriginalPrice * (1 - Number(discount.discount_value || 0) / 100)
        : Math.max(
            0,
            rawOriginalPrice -
              Number(discount.discount_value || 0) * Number(context?.currencyRate || 1)
          );

    return {
      ...option,
      selectedDropdownOptions: {
        variantId: optionVariant?.id,
        discountedPrice: formatPrice(context, rawDiscountedPrice),
        originalPrice: formatPrice(context, rawOriginalPrice),
        rawDiscountedPrice,
        title: optionVariant?.title,
        image: optionVariant?.image || updatedBundleData.matchedProduct?.images_info?.[0]?.src,
      },
    };
  });

  updatedBundleData.selectedOption = updatedBundleData.bundleOptions.find((o) => o.checked);

  // Re-render UI for the currently active option group (single)
  const wrapperItem = target.closest(VOLUME_MENU_ITEM_SELECTOR);
  updateBundleUI(wrapperItem, updatedBundleData, target);
};
